import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { useToast } from '@/hooks/use-toast';
import { emailService } from '@/services/emailService';
import type { EmailTemplate } from '@/types/email';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import EmailHtmlEditor from './EmailHtmlEditor';

// Detecção de variáveis no cliente (apenas feedback; o backend re-deriva os placeholders ao salvar).
function detectarPlaceholders(texto: string): string[] {
  const regex = /\{\{\s*([^}]+?)\s*\}\}/g;
  const seen = new Set<string>();
  const result: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = regex.exec(texto)) !== null) {
    const nome = m[1].trim();
    if (nome && !seen.has(nome)) {
      seen.add(nome);
      result.push(nome);
    }
  }
  return result;
}

// Colunas do boleto oferecidas no mapeamento (mesma ideia do mapeamento de WhatsApp).
const COLUNAS_BOLETO: { value: string; label: string }[] = [
  { value: 'saudacao', label: 'Saudação' },
  { value: 'nome_associado', label: 'Nome do associado' },
  { value: 'placa', label: 'Placa do veículo' },
  { value: 'modelo_veiculo', label: 'Modelo do veículo' },
  { value: 'data_vencimento', label: 'Data de vencimento' },
  { value: 'valor_boleto', label: 'Valor do boleto' },
  { value: 'linha_digitavel', label: 'Linha digitável' },
  { value: 'pix_copia_cola', label: 'PIX copia e cola' },
  { value: 'link_boleto', label: 'Link do boleto' },
  { value: 'nosso_numero', label: 'Nosso número' },
  { value: 'data_pagamento', label: 'Data de pagamento' },
  { value: 'valor_pagamento', label: 'Valor de pagamento' },
  { value: 'beneficios', label: 'Benefícios' },
  { value: 'celular', label: 'Celular' },
  { value: 'codigo_associado', label: 'Código do associado' },
  { value: 'cliente_nome_fantasia', label: 'Nome fantasia (cliente)' },
  { value: 'cliente_cnpj', label: 'CNPJ (cliente)' },
];

interface EmailTemplatesManagerProps {
  clientId: number | undefined;
}

export default function EmailTemplatesManager({ clientId }: EmailTemplatesManagerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [editId, setEditId] = useState<number | null>(null);
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [assunto, setAssunto] = useState('');
  const [corpoHtml, setCorpoHtml] = useState('');
  const [variableMappings, setVariableMappings] = useState<Record<string, string>>({});

  const { data: templates = [], isLoading } = useQuery<EmailTemplate[]>({
    queryKey: ['email-templates', clientId],
    queryFn: () => emailService.listTemplates(clientId),
    enabled: !!clientId,
  });

  function resetForm() {
    setEditId(null);
    setNome('');
    setDescricao('');
    setAssunto('');
    setCorpoHtml('');
    setVariableMappings({});
  }

  const salvar = useMutation({
    mutationFn: () => {
      const placeholdersAtuais = detectarPlaceholders(`${assunto} ${corpoHtml}`);
      const mappingLimpo: Record<string, string> = {};
      for (const p of placeholdersAtuais) {
        if (variableMappings[p]) mappingLimpo[p] = variableMappings[p];
      }
      if (editId) {
        return emailService.atualizarTemplate(editId, {
          nome,
          descricao: descricao || undefined,
          assunto,
          corpo_html: corpoHtml,
          variable_mappings: mappingLimpo,
        });
      }
      return emailService.criarTemplate({
        client_id: clientId,
        nome,
        descricao: descricao || undefined,
        assunto,
        corpo_html: corpoHtml,
        variable_mappings: mappingLimpo,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      toast({ title: editId ? 'Template atualizado' : 'Template criado' });
      resetForm();
    },
    onError: (error: any) => toast({ title: 'Erro', description: error?.message || 'Falha ao salvar o template.', variant: 'destructive' }),
  });

  const remover = useMutation({
    mutationFn: (id: number) => emailService.removerTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      toast({ title: 'Template removido' });
    },
    onError: () => toast({ title: 'Erro', description: 'Falha ao remover o template.', variant: 'destructive' }),
  });

  function editar(t: EmailTemplate) {
    setEditId(t.id);
    setNome(t.nome);
    setDescricao(t.descricao || '');
    setAssunto(t.assunto);
    setCorpoHtml(t.corpo_html);
    setVariableMappings(t.variable_mappings || {});
  }

  const placeholders = detectarPlaceholders(`${assunto} ${corpoHtml}`);
  const previewHtml = DOMPurify.sanitize(corpoHtml);
  const podeEditar = !!clientId;

  return (
    <div className="space-y-6">
      {podeEditar && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle>{editId ? 'Editar template' : 'Novo template'}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="tpl-nome">Nome</Label>
                <Input id="tpl-nome" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Cobrança - 1º aviso" />
              </div>
              <div>
                <Label htmlFor="tpl-desc">Descrição (opcional)</Label>
                <Input id="tpl-desc" value={descricao} onChange={(e) => setDescricao(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="tpl-assunto">Assunto</Label>
                <Input id="tpl-assunto" value={assunto} onChange={(e) => setAssunto(e.target.value)} placeholder="Olá {{nome}}, seu boleto venceu" />
              </div>
              <div>
                <Label>Corpo</Label>
                <EmailHtmlEditor value={corpoHtml} onChange={setCorpoHtml} />
              </div>
              {placeholders.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">Mapeamento das variáveis</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const auto: Record<string, string> = { ...variableMappings };
                        const validos = new Set(COLUNAS_BOLETO.map((c) => c.value));
                        for (const p of placeholders) {
                          if (!auto[p] && validos.has(p.trim())) auto[p] = p.trim();
                        }
                        setVariableMappings(auto);
                      }}
                    >
                      Auto-mapear por nome
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Defina de qual campo do boleto cada variável é preenchida na importação.
                  </p>
                  <div className="space-y-2">
                    {placeholders.map((p) => (
                      <div key={p} className="flex items-center gap-2">
                        <Badge variant="secondary" className="font-mono">{`{{${p}}}`}</Badge>
                        <span className="text-muted-foreground text-sm">→</span>
                        <div className="flex-1">
                          <Select
                            value={variableMappings[p] || ''}
                            onValueChange={(value) =>
                              setVariableMappings((prev) => ({ ...prev, [p]: value }))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o campo do boleto" />
                            </SelectTrigger>
                            <SelectContent>
                              {COLUNAS_BOLETO.map((col) => (
                                <SelectItem key={col.value} value={col.value}>
                                  {col.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={() => salvar.mutate()} disabled={!nome || !assunto || !corpoHtml || salvar.isPending}>
                  {salvar.isPending ? 'Salvando...' : editId ? 'Salvar alterações' : 'Criar template'}
                </Button>
                {editId && (
                  <Button variant="outline" onClick={resetForm} disabled={salvar.isPending}>Cancelar</Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Preview</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-2">
                Assunto: <span className="font-medium text-foreground">{assunto || '—'}</span>
              </p>
              <iframe
                title="Preview do email"
                sandbox=""
                className="w-full min-h-[240px] border rounded bg-white"
                srcDoc={previewHtml || '<p style="color:#999">Preview do corpo...</p>'}
              />
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader><CardTitle>Templates</CardTitle></CardHeader>
        <CardContent>
          {!clientId ? (
            <p className="text-sm text-muted-foreground">
              Selecione um cliente para ver os templates.
            </p>
          ) : isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : templates.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum template cadastrado.</p>
          ) : (
            <div className="space-y-2">
              {templates.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-4 border rounded-lg p-3">
                  <div>
                    <p className="font-medium">{t.nome}</p>
                    <p className="text-sm text-muted-foreground">{t.assunto}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => editar(t)}>Editar</Button>
                    <Button size="sm" variant="destructive" onClick={() => remover.mutate(t.id)} disabled={remover.isPending}>Remover</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
